import {
  CATEGORIES_SET_LIST,
  CATEGORIES_CREATE,
  CATEGORIES_UPDATE,
  CATEGORIES_DELETE,
  CATEGORIES_SHARE_ACCESS,
  CATEGORIES_REMOVE_ACCESS,
  CATEGORIES_SET_ACCESS_LIST,
  CATEGORIES_SET_ALL_ISSUED_ACCESS,
  CATEGORIES_SET_MANAGER_VISIBLE,
  CATEGORIES_SET_CREATE_VISIBLE,
  CATEGORIES_SET_CURRENT,
  CATEGORIES_GET_BY_ID,
  CATEGORIES_ADD_PAGE,
  CATEGORIES_REMOVE_PAGE,
  CATEGORIES_GET_PAGE_CATEGORIES,
} from "../actions/categories";

const state = {
  categories: [],
  currentCategory: null,
  accessList: [],
  allIssuedAccess: [],
  managerVisible: false,
  createVisible: false,
};

const getters = {
  getCategories: (state) => state.categories,
  getCurrentCategory: (state) => state.currentCategory,
  getCategoriesAccessList: (state) => state.accessList,
  getAllIssuedAccess: (state) => state.allIssuedAccess,
  isCategoriesManagerVisible: (state) => state.managerVisible,
  isCategoriesCreateVisible: (state) => state.createVisible,
  getCategoriesByPages: (state) => {
    // This getter will be computed when pages are available
    // We'll handle the pages filtering in the component level to avoid circular dependencies
    return state.categories.filter(category => 
      category.page_ids && category.page_ids.length > 0
    );
  },
};

const mutations = {
  [CATEGORIES_SET_LIST]: (state, categories) => {
    state.categories = categories;
  },
  [CATEGORIES_SET_CURRENT]: (state, category) => {
    state.currentCategory = category;
  },
  [CATEGORIES_SET_ACCESS_LIST]: (state, accessList) => {
    state.accessList = accessList;
  },
  [CATEGORIES_SET_ALL_ISSUED_ACCESS]: (state, issuedAccess) => {
    state.allIssuedAccess = issuedAccess;
  },
  [CATEGORIES_SET_MANAGER_VISIBLE]: (state, visible) => {
    state.managerVisible = visible;
  },
  [CATEGORIES_SET_CREATE_VISIBLE]: (state, visible) => {
    state.createVisible = visible;
  },
};

const actions = {
  [CATEGORIES_SET_LIST]: async ({ commit }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("get-categories", {});
      const categories = response.data.categories || [];
      
      // Ensure all categories have colors assigned
      const { ensureCategoriesHaveColors } = await import("../../utils/categoryColors");
      ensureCategoriesHaveColors(categories);
      
      commit(CATEGORIES_SET_LIST, categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      commit(CATEGORIES_SET_LIST, []);
    }
  },

  [CATEGORIES_GET_BY_ID]: async ({ commit }, categoryId) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("get-category", { id: categoryId });
      return response.data.category;
    } catch (error) {
      console.error("Error fetching category by ID:", error);
      throw error;
    }
  },

  [CATEGORIES_CREATE]: async ({ dispatch }, { name, page_ids }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("categories", { name, page_ids });
      
      // Assign color to the newly created category
      if (response.data && response.data.category && response.data.category.id) {
        const { assignCategoryColor } = await import("../../utils/categoryColors");
        assignCategoryColor(response.data.category.id);
      }
      
      await dispatch(CATEGORIES_SET_LIST);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_UPDATE]: async ({ dispatch }, { id, name, page_ids }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("update-category", { id, name, page_ids });
      await dispatch(CATEGORIES_SET_LIST);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_DELETE]: async ({ dispatch }, categoryId) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      await AxiosModule.Axios.post("delete-category", { id: categoryId });
      await dispatch(CATEGORIES_SET_LIST);
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_SHARE_ACCESS]: async ({ dispatch }, { categoryId, granted_to_email }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("share-category", { id: categoryId, granted_to_email });
      await dispatch(CATEGORIES_SET_ACCESS_LIST, categoryId);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_REMOVE_ACCESS]: async ({ dispatch }, { categoryId, email }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      await AxiosModule.Axios.post("unshare-category", { id: categoryId, email });
      await dispatch(CATEGORIES_SET_ACCESS_LIST, categoryId);
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_SET_ACCESS_LIST]: async ({ commit }, categoryId) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("get-category-access", { id: categoryId });
      commit(CATEGORIES_SET_ACCESS_LIST, response.data.access_list || []);
    } catch (error) {
      console.error("Error fetching category access list:", error);
      commit(CATEGORIES_SET_ACCESS_LIST, []);
    }
  },

  [CATEGORIES_SET_ALL_ISSUED_ACCESS]: async ({ commit }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("get-access-issued", {});
      commit(CATEGORIES_SET_ALL_ISSUED_ACCESS, response.data.issued_accesses || []);
    } catch (error) {
      console.error("Error fetching all issued access:", error);
      commit(CATEGORIES_SET_ALL_ISSUED_ACCESS, []);
    }
  },

  [CATEGORIES_ADD_PAGE]: async ({ dispatch }, { page_id, category_id }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("add-page-to-category", { page_id, category_id });
      await dispatch(CATEGORIES_SET_LIST);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_REMOVE_PAGE]: async ({ dispatch }, { page_id, category_id }) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("remove-page-from-category", { page_id, category_id });
      await dispatch(CATEGORIES_SET_LIST);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  [CATEGORIES_GET_PAGE_CATEGORIES]: async ({ commit }, page_id) => {
    try {
      const { default: AxiosModule } = await import("../../models/AxiosModule");
      const response = await AxiosModule.Axios.post("get-page-categories", { page_id });
      return response.data.categories || [];
    } catch (error) {
      console.error("Error fetching page categories:", error);
      throw error;
    }
  },
};

export default {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
}; 